import React, { DetailedHTMLProps, HTMLAttributes, HTMLProps, SyntheticEvent, useMemo } from 'react';

import { makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';

import './composable-list-item.scss';
import Field, { FieldDefinition } from './field';

interface IComposableListItemClasses {
  wrapper: string;
  item: string;
}

export interface IComposableListItem {
  targetUrl?: string;
  trackingData?: Record<string, string>;
  onClick?: (ev: SyntheticEvent, trackingData?: Record<string, string>) => void;
}

export type OmittedComposableItem<D extends IComposableListItem> = Omit<D, 'onClick' | 'targetUrl' | 'trackingData'>;

export interface IComposableListItemProps<D extends IComposableListItem>
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement | HTMLAnchorElement>, HTMLDivElement | HTMLAnchorElement> {
  fields: Array<FieldDefinition<OmittedComposableItem<D>>>;
  data: D;
  inverted?: boolean;
  className?: string;
}

const classNames = (inverted: boolean, additionalWrapperClasses?: string): IComposableListItemClasses =>
  makeClassNames({
    wrapper: ['composable-list-item', inverted && 'composable-list-item--inverted', additionalWrapperClasses],
    item: 'composable-list-item__field',
  }) as IComposableListItemClasses;

function ComposableListItem<D extends IComposableListItem>({
  fields,
  data,
  inverted = false,
  className,
  ...htmlProps
}: IComposableListItemProps<D>) {
  const cn = useMemo<IComposableListItemClasses>(() => classNames(inverted, className), [inverted, className]);
  const itemTag = useMemo<string>(() => (data.targetUrl ? 'a' : 'div'), [data]);

  const props = useMemo<HTMLProps<HTMLAnchorElement>>(() => {
    const { onClick, targetUrl, trackingData, ...rest } = data;
    return {
      ...htmlProps,
      className: cn.wrapper,
      href: targetUrl,
      ...(trackingData ? objectToDataAttributes(trackingData) : {}),
      onClick: (e) => onClick?.(e, trackingData),
      children: fields.map((field, index) => {
        return (
          <Field<D>
            field={field}
            // @ts-ignore
            data={rest}
            key={`${index}-${String(typeof field === 'object' ? field.prop : field)}`}
            className={cn.item}
            aria-hidden={Boolean(htmlProps.title)}
          />
        );
      }),
    } as HTMLProps<HTMLAnchorElement>;
  }, [data, fields, cn, htmlProps]);

  return React.createElement(itemTag, props);
}

export default ComposableListItem;
